import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/apiClient";
import {
  createEntryApiV1AdminAccessListPost,
  deleteEntryApiV1AdminAccessListEntryIdDelete,
  updateEntryApiV1AdminAccessListEntryIdPatch,
} from "@/api/generated/sdk.gen";
import type {
  ApprovalEntryCreateSchema,
  ApprovalEntryUpdateSchema,
} from "@/api/generated/types.gen";

import { CLIENT_KEYS } from "@constants/clientKeys";

const errText = (error: unknown): string =>
  (error as { detail?: string })?.detail || "Request failed.";

/** Add a user to the approval list (admin). */
export const useCreateAccessEntry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: ApprovalEntryCreateSchema) => {
      const response = await createEntryApiV1AdminAccessListPost({
        client: apiClient,
        body,
      });
      if ("error" in response && response.error) {
        throw new Error(errText(response.error));
      }
      return response.data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [CLIENT_KEYS.ACCESS_LIST] }),
  });
};

/** Update an entry: suspend/activate, change access level or user type. */
export const useUpdateAccessEntry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      entryId,
      body,
    }: {
      entryId: string;
      body: ApprovalEntryUpdateSchema;
    }) => {
      const response = await updateEntryApiV1AdminAccessListEntryIdPatch({
        client: apiClient,
        path: { entry_id: entryId },
        body,
      });
      if ("error" in response && response.error) {
        throw new Error(errText(response.error));
      }
      return response.data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [CLIENT_KEYS.ACCESS_LIST] }),
  });
};

/** Remove an entry from the approval list (admin). */
export const useDeleteAccessEntry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (entryId: string) => {
      const response = await deleteEntryApiV1AdminAccessListEntryIdDelete({
        client: apiClient,
        path: { entry_id: entryId },
      });
      if ("error" in response && response.error) {
        throw new Error(errText(response.error));
      }
      return true;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [CLIENT_KEYS.ACCESS_LIST] }),
  });
};
